import React, { useCallback, useMemo, useState } from 'react'
import SectionHeader from '@ringdna/common/src/components/RdnaHeaderSection'
import styled from 'styled-components'
import RdnaButton from '@ringdna/common/src/components/RdnaButton'
import AddUsersButton from './AddUsersButton'
import UsersTable from './UsersTable'
import {
  getLicenseFilter,
  initLicenseFilter,
  initUserStatusFilter,
  License,
  Option,
  PAGE_SIZE,
  RtnPagination,
  SERVER_PAGE_OFFSET,
  userSortDirection,
  userSortKeys,
  UserStatus
} from './utils'
import AddUsers from './add-users/AddUsers'
import QuickAdd from './QuickAdd'
import { useAvailableLicenses, useLocations, useUsers } from '../../../store/api/users-api'
import { useAccount } from '../../../store/api/accounts-api'
import LicenseManagement from './LicenseManagement'

type Props = {
  selectedAccountId: number | null
  userAccountId: number
}

const HEAD = 'Users'
const SUB_HEAD = 'Add new Revenue.io users and manage product licenses in bulk.'

export default function UserManagement({ selectedAccountId, userAccountId }: Props) {
  const [selectedAccount] = useAccount({ meta: { id: (selectedAccountId || userAccountId).toString() } })
  const [licenses, , , refetchLicenses] = useAvailableLicenses({ meta: { id: selectedAccountId || userAccountId } })
  const [locations] = useLocations({ meta: { id: selectedAccountId || userAccountId } })
  const [userStatusFilter, setUserStatusFilter] = useState<Option | undefined>(
    initUserStatusFilter(window.location.hash)
  )
  const [licenseFilter, setLicenseFilter] = useState<string[]>(initLicenseFilter(window.location.hash) as string[])
  const [showQuickAddPanel, setShowQuickAddPanel] = useState(false)
  const [showAddUsersPanel, setShowAddUsersPanel] = useState(false)
  const [showLicenseManagementPanel, setShowLicenseManagementPanel] = useState(false)

  const [queryState, setQueryState] = useState<RtnPagination>({
    globalFilter: '',
    sortBy: [{ id: 'createdDate', desc: true }],
    pageIndex: 0,
    pageSize: PAGE_SIZE
  })

  const [payload, , loading, refetch] = useUsers({
    query: {
      accountId: selectedAccountId,
      userStatus: userStatusFilter?.value || UserStatus.ALL,
      globalFilter: queryState.globalFilter,
      ...getLicenseFilter(licenseFilter),
      'pager.pageSize': queryState.pageSize,
      'pager.currentPage': queryState.pageIndex + SERVER_PAGE_OFFSET,
      'pager.sortBy': userSortKeys(queryState.sortBy[0]?.id),
      'pager.sortDirection': userSortDirection(queryState.sortBy[0]?.desc)
    }
  })

  const countLicenses = (type: string) => {
    // @ts-ignore
    const count = licenses ? licenses[`${type}UserLimit`] - licenses[`${type}UserCount`] : 0
    return count < 0 ? 0 : count
  }

  const remainingLicenses = useMemo(() => {
    return {
      [License.ADMIN]: countLicenses('admin'),
      [License.INTELLIGENT_DIALER]: countLicenses('dialer'),
      [License.CALENDAR]: countLicenses('nylas'),
      [License.CONVERSATION_AI]: countLicenses('cai'),
      [License.GUIDED_SELLING]: countLicenses('guidedSelling'),
      [License.MOMENTS]: countLicenses('moments'),
      [License.REVENUE_AI]: countLicenses('revenueAI')
    }
  }, [countLicenses])

  const handleUserStatusFilterChange = (userStatus: Option | undefined) => {
    setUserStatusFilter(userStatus)
    setQueryState({ ...queryState, pageIndex: 0 })
  }

  const handleLicenseFilterChange = (license: string[]) => {
    setLicenseFilter(license)
    setQueryState({ ...queryState, pageIndex: 0 })
  }

  const handleQueryState = useCallback(
    (state: RtnPagination) => {
      setQueryState(state)
    },
    [setQueryState]
  )

  const onLicenseManagementClick = () => {
    setShowLicenseManagementPanel(true)
  }

  const onAddUsersClick = () => {
    setShowAddUsersPanel(true)
  }

  const onQuickAddClick = () => {
    setShowQuickAddPanel(true)
  }

  const onSaveQuickAdd = (reload: boolean) => {
    setShowQuickAddPanel(false)
    reload && refreshData()
  }

  const refreshData = () => {
    refetch()
    refetchLicenses()
  }

  return (
    <Container>
      <SectionHeader
        headingText={HEAD}
        descriptionText={SUB_HEAD}
        rightColumn={
          selectedAccountId && (
            <div>
              <RdnaButton text={'License Management'} variant={'outlined'} onClick={onLicenseManagementClick} />
              {selectedAccountId === userAccountId ? (
                <AddUsersButton onQuickAddClick={onQuickAddClick} onAddUsersClick={onAddUsersClick} />
              ) : (
                <RdnaButton text={'Quick Add'} onClick={onQuickAddClick} />
              )}
            </div>
          )
        }
      />
      <UsersTable
        payload={payload}
        onQueryState={handleQueryState}
        loading={!!loading}
        userStatusFilter={userStatusFilter}
        handleUserStatusFilterChange={handleUserStatusFilterChange}
        licenseFilter={licenseFilter}
        handleLicenseFilterChange={handleLicenseFilterChange}
        selectedAccountId={selectedAccountId}
        globalFilter={queryState.globalFilter}
      />
      {selectedAccount && locations && (
        <QuickAdd
          isOpen={showQuickAddPanel}
          onClose={() => setShowQuickAddPanel(false)}
          selectedAccount={selectedAccount}
          onSave={onSaveQuickAdd}
          remainingLicenses={remainingLicenses}
          locations={locations}
        />
      )}
      {showAddUsersPanel && locations && selectedAccount && (
        <AddUsers
          isOpen={showAddUsersPanel}
          onClose={() => setShowAddUsersPanel(false)}
          remainingLicenses={remainingLicenses}
          locations={locations}
          selectedAccount={selectedAccount}
          onSave={(reload: boolean) => {
            reload && refreshData()
          }}
        />
      )}
      {showLicenseManagementPanel && selectedAccount && (
        <LicenseManagement
          selectedAccount={selectedAccount}
          isOpen={showLicenseManagementPanel}
          onClose={() => setShowLicenseManagementPanel(false)}
          remainingLicenses={remainingLicenses}
          onSave={(reload: boolean) => {
            setShowLicenseManagementPanel(false)
            reload && refreshData()
          }}
        />
      )}
    </Container>
  )
}

const Container = styled.div`
  margin-top: ${({ theme }) => theme.spacing * 3}px;
`
